import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  CircularProgress,
  Chip,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Wand2 } from 'lucide-react';
import generalApi, { setGlobalRefreshLimits } from '../api/generalApi';
import { useLimits } from '../context/LimitsContext';
import profileApi from '../api/profileApi';
import character0 from '../assets/characters/0.png';
import SubscriptionModal from './common/SubscriptionModal';
import AddToProjectModal from './Project/AddToProjectModal';
import MobileAddToProjectModal from './Project/MobileAddToProjectModal';
import { useMediaQuery, useTheme } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';

const THEME_COLOR = '#ff914d';
const BOT_NAME = 'Mich-AI-Langelo';
const BOT_GROUP = 'Image Creator';
const BOT_DESCRIPTION = 'Transform your ideas into stunning visuals with AI-powered image generation. Perfect for creating unique artwork, illustrations, and visual concepts for your projects.';

const styles = {
  styleChip: {
    margin: '4px',
    backgroundColor: `${THEME_COLOR}15`,
    color: THEME_COLOR,
    borderColor: `${THEME_COLOR}40`,
    cursor: 'pointer',
    '&.MuiChip-filled': {
      backgroundColor: THEME_COLOR,
      color: 'white',
      '&:hover': {
        backgroundColor: `${THEME_COLOR}dd`,
      },
    },
    '&:hover': {
      backgroundColor: `${THEME_COLOR}20`,
    },
  }
};

const ImageGeneration = () => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState('');
  const [lastSuccessfulPrompt, setLastSuccessfulPrompt] = useState('');
  const { limits, fetchLimits } = useLimits();
  const [showAddToProjectModal, setShowAddToProjectModal] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Set up the global refresh limits function
  useEffect(() => {
    setGlobalRefreshLimits(fetchLimits);
    return () => setGlobalRefreshLimits(null);
  }, [fetchLimits]);

  const isImagesAllowed = limits?.imagesAllowed;

  const handleSubscriptionClick = async () => {
    try {
      const url = await profileApi.getWordPressLoginUrl();
      setSubscriptionUrl(url);
      setShowSubscriptionModal(true);
    } catch (error) {
      toast.error('Error loading subscription page. Please try again.');
    }
  };

  // Add cleanup effect for generated image URLs
  useEffect(() => {
    return () => {
      generatedImages.forEach(url => {
        if (typeof url === 'string' && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [generatedImages]);

  const enhancementTags = [
    // Quality and Detail
    'high-quality',
    'ultra detailed',
    'sharp focus',
    'high resolution',
    'masterpiece',
    'best quality',
    
    // Lighting and Atmosphere
    'dramatic lighting',
    'cinematic lighting',
    'studio lighting',
    'soft lighting',
    'natural lighting',
    'golden hour',
    'volumetric lighting',
    
    // Style and Mood
    'photorealistic',
    'professional',
    'cinematic',
    'epic',
    'elegant',
    'vibrant colors',
    'rich colors',
    'moody',
    
    // Camera and Composition
    'bokeh',
    'depth of field',
    'wide angle',
    'close-up',
    'macro',
    '8k uhd',
    
    // Art Styles
    'digital art',
    'concept art',
    'illustration',
    'oil painting',
    'watercolor',
    'anime style',
    'cartoon style',
    'realistic',
    '3d render'
  ];

  const handleStyleTagToggle = (tag) => {
    setSelectedStyles(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    // Validate prompt length (200 words max)
    const wordCount = prompt.trim().split(/\s+/).length;
    if (wordCount > 200) {
      toast.error('Prompt exceeds maximum length of 200 words');
      return;
    }

    setLoading(true);
    try {
      // Combine prompt with any selected enhancement tags
      const fullPrompt = selectedStyles.length > 0
        ? `${prompt}, ${selectedStyles.join(', ')}`
        : prompt;
      
      const response = await generalApi.images.generateImage({
        prompt: fullPrompt,
        guidance: 7.5,
      });

      if (response.success && response.images && Array.isArray(response.images)) {
        // Ensure we only store valid URLs
        const validImages = response.images.filter(url => typeof url === 'string');
        setGeneratedImages(validImages);
        if (validImages.length > 0) {
          setLastSuccessfulPrompt(prompt.trim());
          // Fetch limits immediately after successful generation
          await fetchLimits(true);
          
          // Set up a delayed fetch after 2 seconds to ensure all updates are captured
          await new Promise(resolve => setTimeout(resolve, 2000));
          await fetchLimits(true);
          
          // Show success message after limits have been updated
          toast.success('Image generated successfully!');
        } else {
          toast.error('No valid images were generated');
        }
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error generating images:', error);
      toast.error(error.response?.data?.error || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToProject = () => {
    if (!generatedImages.length > 0) return;
    setShowAddToProjectModal(true);
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ 
        py: 2,
        mt: 2,
        height: { xs: 'auto', md: 'calc(100vh - 85px)' },
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Card
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: `0 8px 32px -8px ${THEME_COLOR}30`,
            border: `1px solid ${THEME_COLOR}40`,
            overflow: 'hidden',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Grid container sx={{ 
            flex: 1,
            overflow: { xs: 'visible', md: 'hidden' },
            height: { xs: 'auto', md: '100%' }
          }}>
            {/* Left Column - Form */}
            <Grid item xs={12} md={5} sx={{ 
              borderRight: { xs: 'none', md: `1px solid ${THEME_COLOR}40` },
              borderBottom: { xs: `1px solid ${THEME_COLOR}40`, md: 'none' },
              borderColor: `${THEME_COLOR}40`,
              display: 'flex',
              flexDirection: 'column',
              height: { xs: 'auto', md: '100%' },
              overflow: 'auto',
            }}>
              <Box sx={{ p: 4 }}>
                {/* Bot Info Section */}
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  mb: 2,
                  pt: 3,
                }}>
                  <Avatar
                    src={character0}
                    sx={{ 
                      width: 100,
                      height: 100,
                      border: `2px solid ${THEME_COLOR}`,
                      mt: "10px",
                      boxShadow: `0 4px 20px -8px ${THEME_COLOR}80`,
                    }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700,
                      color: THEME_COLOR,
                      fontSize: '1.75rem',
                      mb: 0.2,
                    }}>
                      {BOT_NAME}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ 
                      color: 'text.secondary',
                      fontSize: '1.15rem',
                    }}>
                      {BOT_GROUP}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  mb: 2,
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                }}>
                  {BOT_DESCRIPTION}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Image Description"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={!isImagesAllowed}
                    placeholder={isImagesAllowed ? "Describe the image you want to generate..." : "Upgrade to generate images"}
                    sx={{ 
                      mb: 3,
                      '& .MuiInputBase-input': {
                        fontSize: '1.1rem',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '1.1rem',
                        '&.Mui-focused': {
                          color: THEME_COLOR,
                        },
                      },
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: THEME_COLOR,
                          borderWidth: 2,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: `${THEME_COLOR}90`,
                        },
                      },
                    }}
                  />

                  <Typography variant="subtitle2" gutterBottom sx={{ 
                    fontSize: '1.1rem',
                    mb: 1.5,
                    fontWeight: 600,
                  }}>
                    Enhancement Tags (can select multiple, or none)
                  </Typography>
                  <Box sx={{ 
                    mb: 3, 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    opacity: isImagesAllowed ? 1 : 0.5,
                    pointerEvents: isImagesAllowed ? 'auto' : 'none',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${THEME_COLOR} transparent`,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: THEME_COLOR,
                      borderRadius: '3px',
                      '&:hover': {
                        backgroundColor: `${THEME_COLOR}dd`,
                      },
                    },
                  }}>
                    {enhancementTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="medium"
                        clickable
                        color="primary"
                        variant={selectedStyles.includes(tag) ? "filled" : "outlined"}
                        onClick={() => handleStyleTagToggle(tag)}
                        sx={styles.styleChip}
                      />
                    ))}
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading || !isImagesAllowed}
                    onClick={!isImagesAllowed ? handleSubscriptionClick : undefined}
                    sx={{
                      height: 48,
                      backgroundColor: THEME_COLOR,
                      '&:hover': {
                        backgroundColor: `${THEME_COLOR}dd`,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'action.disabledBackground',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : !isImagesAllowed ? (
                      'Upgrade to Generate Images'
                    ) : (
                      <>
                        <Wand2 size={18} style={{ marginRight: 8 }} />
                        Generate Image (25 credits)
                      </>
                    )}
                  </Button>
                </form>
              </Box>
            </Grid>

            {/* Right Column - Output */}
            <Grid item xs={12} md={7} sx={{ 
              display: 'flex',
              flexDirection: 'column',
              height: { xs: 'auto', md: '100%' },
              overflow: { xs: 'visible', md: 'hidden' },
              minHeight: { xs: '500px', md: 'auto' },
            }}>
              <Box sx={{ 
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: 3, // Add consistent gap
              }}>
                <Typography variant="h6" sx={{ 
                  color: THEME_COLOR, 
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  pt: 3,
                }}>
                  Generated Image
                </Typography>

                {generatedImages.length > 0 && (
                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                  }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveToProject}
                      startIcon={<FolderIcon />}
                      sx={{
                        bgcolor: THEME_COLOR,
                        '&:hover': {
                          bgcolor: `${THEME_COLOR}dd`,
                        },
                      }}
                    >
                      Save to Project
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        const sanitizedPrompt = lastSuccessfulPrompt
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-+|-+$/g, '')
                          || 'generated-image';
                        const link = document.createElement('a');
                        link.href = generatedImages[0];
                        link.download = `${sanitizedPrompt}.jpg`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      sx={{
                        bgcolor: THEME_COLOR,
                        '&:hover': {
                          bgcolor: `${THEME_COLOR}dd`,
                        },
                      }}
                    >
                      Download
                    </Button>
                  </Box>
                )}
                
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 0,
                  overflow: 'hidden',
                }}>
                  {loading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 450,
                        height: 450,
                        border: `1px solid ${THEME_COLOR}40`,
                        backgroundColor: 'rgba(0,0,0,0.03)',
                      }}
                    >
                      <CircularProgress sx={{ color: THEME_COLOR }} />
                    </Box>
                  ) : generatedImages.length > 0 ? (
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      overflow: 'auto',
                    }}>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        minHeight: { xs: '300px', sm: '450px' },
                        padding: { xs: 2, sm: 0 },
                      }}>
                        <img
                          src={generatedImages[0]}
                          alt="Generated image"
                          loading="lazy"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            width: 'auto',
                            height: 'auto',
                          }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 450,
                        height: 450,
                        backgroundColor: 'rgba(0,0,0,0.03)',
                        border: `1px solid ${THEME_COLOR}40`,
                      }}
                    >
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary',
                        fontSize: '1.1rem',
                        position: 'relative',
                        zIndex: 1,
                        display: isImagesAllowed ? 'block' : 'none',
                      }}>
                        Generated image will appear here
                      </Typography>
                      
                      {/* Upgrade Button Overlay for disabled state */}
                      {!isImagesAllowed && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0.85)',
                            backdropFilter: 'blur(4px)',
                            borderRadius: '4px',
                            gap: 2,
                            p: 4,
                            textAlign: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              zIndex: 11,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                              background: 'rgba(0, 0, 0, 0.3)',
                              p: 4,
                              borderRadius: '12px',
                              backdropFilter: 'blur(8px)',
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                color: 'white',
                                mb: 1,
                                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                fontWeight: 600,
                              }}
                            >
                              Unlock AI Image Generation
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: 'rgba(255,255,255,0.95)',
                                mb: 1,
                                fontSize: '1.1rem',
                                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              }}
                            >
                              Upgrade your plan to create stunning AI-generated images
                            </Typography>
                            <Button
                              variant="contained"
                              onClick={handleSubscriptionClick}
                              sx={{
                                bgcolor: '#be58da',
                                color: 'white',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                '&:hover': {
                                  bgcolor: '#a94cc3',
                                  transform: 'translateY(-2px)',
                                },
                                boxShadow: '0 0 20px rgba(190, 88, 218, 0.5)',
                                border: '2px solid rgba(190, 88, 218, 0.8)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                zIndex: 12,
                              }}
                            >
                              Upgrade Now
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Container>

      <SubscriptionModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        subscriptionUrl={subscriptionUrl}
      />

      {isMobile ? (
        <MobileAddToProjectModal
          open={showAddToProjectModal}
          onClose={() => setShowAddToProjectModal(false)}
          contentType="Image"
          contentValue={generatedImages[0] || ''}
        />
      ) : (
        <AddToProjectModal
          open={showAddToProjectModal}
          onClose={() => setShowAddToProjectModal(false)}
          contentType="Image"
          contentValue={generatedImages[0] || ''}
        />
      )}
    </>
  );
};

export default ImageGeneration; 